import { addSortAndFilterParams, formatDateForBackend } from "~/utils";
import type { Admin } from "./admins";
import { api, getAuthHeaders } from "./axios";
import type { ServiceResponse } from "./axios";
import type { Insole } from "./insoles";
import type { LastMeasurements } from "./lastMeasurements";
import type { PhysicalFitnessAssessment } from "./physicalFitnessAssessments";
import type {
  MessageResponse,
  PaginationParams,
  SearchParams,
  SortingParams,
} from "./types";
import type { Unit } from "./units";

export interface Patient {
  id: number;
  email: string;
  name: string;
  surname: string;
  date_of_birth: string;
  sex_at_birth: string;
  height_inches: number;
  shoe_size_us: number;
  arch_type: string;
  podiatrist_name: string;
  next_test_date?: string;
  last_physical_fitness_assessment_test?: PhysicalFitnessAssessment;
  active_insole?: Exclude<Insole, "patient">;
  unit?: Unit;
  last_measurements: LastMeasurements;
}

export interface PatientInvitation {
  email: string;
  expires_at: string;
  invited_by: Admin;
  unit?: Unit;
}

interface UnassignPatientFromUnitParams {
  patientId: number;
  unitId: number;
}

export const unassignPatientFromUnit = async ({
  patientId,
  unitId,
}: UnassignPatientFromUnitParams) => {
  const response = await api.delete<ServiceResponse<Patient>>(
    `/patients/${patientId}/units`,
    {
      headers: getAuthHeaders(),
      params: { unit_id: unitId },
    },
  );

  return response.data;
};

interface UpdatePatientParams {
  patientId: number;
  unitId: number | null;
  nextTestDate: Date | null;
}

export const updatePatient = async ({
  patientId,
  unitId,
  nextTestDate,
}: UpdatePatientParams) => {
  const response = await api.patch<ServiceResponse<Patient[]>>(
    `/patients/${patientId}`,
    {
      next_test_date: nextTestDate ? formatDateForBackend(nextTestDate) : null,
      unit_id: unitId,
    },
    { headers: getAuthHeaders() },
  );

  return response.data;
};

export const getPatients = async ({
  page,
  per_page,
  search,
  order_by,
  sort_direction,
}: PaginationParams & SearchParams & SortingParams) => {
  const params: Record<string, string | number> = {
    page,
    per_page,
  };

  const fullParams = addSortAndFilterParams(
    search,
    order_by,
    sort_direction,
    params,
  );

  const { data } = await api.get<ServiceResponse<Patient[]>>("/patients", {
    headers: getAuthHeaders(),
    params: fullParams,
  });

  return data;
};

export interface GetPendingInvitationsResponse {
  email: string;
  invited_by: Admin;
  expires_at: string;
}

export const getPendingInvitations = async ({
  page,
  per_page,
  search,
  order_by,
  sort_direction,
}: PaginationParams & SearchParams & SortingParams) => {
  const params: Record<string, string | number> = {
    page,
    per_page,
  };

  const fullParams = addSortAndFilterParams(
    search,
    order_by,
    sort_direction,
    params,
  );

  const { data } = await api.get<
    ServiceResponse<GetPendingInvitationsResponse[]>
  >("/patients/invitations", {
    headers: getAuthHeaders(),
    params: fullParams,
  });

  return data;
};

export const getPatient = async (id: number) => {
  const { data } = await api.get<ServiceResponse<Patient>>(`/patients/${id}`, {
    headers: getAuthHeaders(),
  });

  return data;
};

interface InvitePatientParams {
  email: string;
  name: string;
  surname: string;
  password?: string;
  force_change_password?: boolean;
}

export const invitePatient = async ({
  email,
  name,
  surname,
  password,
  force_change_password,
}: InvitePatientParams) => {
  const params: Record<string, string | number | boolean> = {
    email,
    name,
    surname,
  };

  if (force_change_password) {
    params.force_change_password = force_change_password;
  }

  if (password) {
    params.password = password;
  }

  const response = await api.post<ServiceResponse<PatientInvitation>>(
    "/patients/invitations",
    params,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

export const deleteInvitation = async (email: string) => {
  const response = await api.delete<ServiceResponse<MessageResponse>>(
    "/patients/invitations",
    {
      headers: getAuthHeaders(),
      params: { email },
    },
  );
  return response.data;
};
